/*@flow*/
/** @jsx jsx */
import { jsx, css } from '@emotion/core';
import VegaLiteFlex from 'comps/App/VegaLite/Flex';
import ColumnGrid from '../ColumnGrid';
import { useState, useContext, useEffect } from 'react';
import useCartoMap from 'lib/hooks/useCartoMap';
import Category from '../Category';
import { extractVectorTiles } from 'lib/getCartoTiles';
import { ChartDeckContext } from '../contexts';
import Present from 'lib/Present';
import keepNonEmptyKeys from 'lib/keepNonEmptyKeys';
import { type VectorTileUrls } from 'lib/getCartoMap';
import hash from 'lib/hash';
import ChartContainer from '../ChartContainer';
import ClearButton from '../ClearButton';
import sensors from './sensors';
import mkPopupEvents from '../mkPopupEvents';

let CHART_NAME;

const sensorMarkerDecks = [
  {
    id: 'numina-sensors',
    type: 'layer',
    config: {
      type: 'circle',
      source: {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: sensors.map(v => {
            const { location, name } = v.node;
            const loc = location;
            return {
              type: 'Feature',
              properties: { name },
              geometry: { type: 'Point', coordinates: [loc.lon, loc.lat] },
            };
          }),
        },
      },
      paint: {
        'circle-radius': 5,
        'circle-color': 'red',
      },
    },
    events: mkPopupEvents(
      v => `
      <div>
        <h4>${CHART_NAME}</h4>
        <div><b>Name</b>: ${v.name}</div>
      </div>
    `
    ),
  },
];

const QUERY_TMPL = () => `
  WITH
  A00 as (
    SELECT * FROM new_lab_numina
  ),
  A0 as (
    SELECT the_geom_webmercator, class, date_trunc('hour', time) as hour
    FROM A00
  ),
  A1 as (
    SELECT *, count(hour) OVER w
    FROM A0
    WINDOW w AS (PARTITION BY hour, class)
  ),
  A2 as (
    SELECT distinct hour, class, count FROM A1
  )
  SELECT ST_MakePoint(40.689305, -73.981828) as the_geom_webmercator, count, hour, class FROM A2 ORDER BY hour ASC
`;

const deviceName = 'DBP-4 - Fulton St & Hanover Pl mid-block';

const VEGA_LITE_SPEC = {
  $schema: 'https://vega.github.io/schema/vega-lite/v3.json',
  autosize: 'fit',
  padding: { left: 10, top: 10, right: 10, bottom: 10 },

  title: `${deviceName} (2019-02-25)`,

  layer: [
    {
      encoding: {
        x: {
          field: 'hour',
          type: 'temporal',
          axis: { format: '%I %p', title: 'Time (Eastern Standard Time)' },
        },
        y: {
          field: 'count',
          type: 'quantitative',
          axis: { title: 'count', format: 'f' },
        },
        color: {
          field: 'class',
          type: 'nominal',
          legend: {
            title: null,
            padding: 10,
          },
        },
      },
      layer: [
        {
          mark: 'line',
        },
        {
          mark: 'point',
          selection: {
            labelSelection: {
              type: 'single',
              nearest: true,
              on: 'mouseover',
              encodings: ['x'],
              empty: 'none',
            },
            gridSelection: {
              type: 'interval',
              bind: 'scales',
              encodings: ['x', 'y'],
            },
          },
        },
      ],
    },
    {
      transform: [
        {
          filter: {
            selection: 'labelSelection',
          },
        },
      ],
      layer: [
        {
          mark: {
            type: 'rule',
            color: 'gray',
          },
          encoding: {
            x: {
              type: 'temporal',
              field: 'hour',
            },
          },
        },
        {
          encoding: {
            text: {
              type: 'quantitative',
              field: 'count',
            },
            x: {
              type: 'temporal',
              field: 'hour',
            },
            y: {
              type: 'quantitative',
              field: 'count',
            },
          },
          layer: [
            {
              mark: {
                type: 'text',
                stroke: 'white',
                strokeWidth: 2,
                align: 'left',
                dx: 5,
                dy: -5,
              },
            },
            {
              mark: {
                type: 'text',
                align: 'left',
                dx: 5,
                dy: -5,
              },
              encoding: {
                fill: {
                  type: 'nominal',
                },
              },
            },
          ],
        },
      ],
    },
  ],
};

const removeZ = v => ({
  ...v,
  hour: v.hour.replace(/Z$/, ''),
});

const LineChart = props => {
  const { mapConfig, analysisId } = props;
  return (
    <ChartContainer mapConfig={mapConfig} analysisId={analysisId}>
      {({ data }) => {
        // Remove Zulu timezone.
        const values = data.map(removeZ);
        return (
          <VegaLiteFlex
            data={{
              values,
            }}
            spec={VEGA_LITE_SPEC}
          />
        );
      }}
    </ChartContainer>
  );
};

const DATAVIEW_1 = 'numina-widget';
const ANALYSIS_1 = 'numina-data';
const LAYER_1 = 'numina';

const INIT_CONFIG = {
  buffersize: {
    mvt: 0,
  },
  analyses: [
    {
      id: ANALYSIS_1,
      type: 'source',
      params: {
        query: QUERY_TMPL(),
      },
    },
  ],
  layers: [
    {
      id: LAYER_1,
      type: 'cartodb',
      options: {
        source: {
          id: ANALYSIS_1,
        },
      },
    },
  ],
  dataviews: {
    [DATAVIEW_1]: {
      type: 'aggregation',
      source: {
        id: ANALYSIS_1,
      },
      options: {
        aggregation: 'max',
        column: 'class',
        aggregationColumn: 'count',
      },
    },
  },
};

const getMapboxTiles = ({
  mapTiles,
  cartoLayerName,
}: {
  mapTiles: VectorTileUrls,
  cartoLayerName: string,
}) => [
  ...sensorMarkerDecks,
  {
    id: `${cartoLayerName}-source`,
    type: 'source',
    config: {
      type: 'vector',
      tiles: mapTiles,
      maxzoom: 18,
    },
  },
  {
    id: `${cartoLayerName}-layer`,
    type: 'layer',
    config: {
      type: 'circle',
      source: `${cartoLayerName}-source`,
      'source-layer': cartoLayerName,
    },
  },
];

export default (props: { name: string }) => {
  CHART_NAME = props.name;
  const [params, setParams] = useState({});
  const [showLayers, setShowLayers] = useState(false);
  const mapConfig = useCartoMap(params, INIT_CONFIG);
  const mapTiles = mapConfig ? extractVectorTiles(mapConfig) : null;
  const { addMapTiles, removeMapTiles } = useContext(ChartDeckContext);

  useEffect(() => {
    if (mapTiles) {
      const tiles = getMapboxTiles({
        mapTiles,
        cartoLayerName: LAYER_1,
      });
      if (showLayers) {
        addMapTiles(tiles);
      } else {
        removeMapTiles(tiles);
      }
    }
  }, [mapTiles && mapTiles[0], showLayers, hash(params)]);

  useEffect(() => {
    setTimeout(() => Present.is(params.filters) && setShowLayers(true), 100);
  }, [hash(params.filters)]);

  return (
    <div
      css={css(
        `
      display: grid;
      grid-template-rows: 2rem 1fr;
      grid-column-gap: 10px;
      `
      )}
    >
      <div css={css('padding-top: 5px; padding-bottom: 5px;')}>
        <label css={css('margin: 0; cursor: pointer;')}>
          <input
            type="checkbox"
            checked={showLayers}
            onChange={e => setShowLayers(e.target.checked)}
          />{' '}
          Show map layers
        </label>
        <ClearButton
          filters={params.filters}
          onClick={() => {
            setParams({
              filters: {},
            });
          }}
        />
      </div>
      <ColumnGrid columns="calc(100% - 320px) 300px" padBottom="20px">
        <div css={css('height: 300px; width: 95%;')}>
          <LineChart mapConfig={mapConfig} analysisId={ANALYSIS_1} />
        </div>
        <div>
          <Category
            title="Objects"
            id={DATAVIEW_1}
            mapConfig={mapConfig}
            onFiltersChanged={({ accepted }) => {
              const filters = keepNonEmptyKeys({
                accept: accepted,
                reject: [],
              });
              if (Present.is(filters)) {
                setParams({
                  filters: {
                    dataviews: {
                      [DATAVIEW_1]: filters,
                    },
                  },
                });
              }
            }}
          />
        </div>
      </ColumnGrid>
    </div>
  );
};
